import React, { useState } from 'react';
import {
  Cog6ToothIcon,
  BuildingOfficeIcon,
  CurrencyDollarIcon,
  ClockIcon,
  DocumentTextIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';

const Settings = () => {
  const [activeTab, setActiveTab] = useState('general');
  const [settings, setSettings] = useState({
    general: {
      organizationName: 'MicroFinance SA',
      currency: 'HTG',
      timezone: 'Africa/Dakar',
      dateFormat: 'DD/MM/YYYY',
      language: 'fr',
    },
    loan: {
      defaultInterestRate: 12,
      maxLoanAmount: 1000000,
      minLoanTerm: 3,
      maxLoanTerm: 36,
      gracePeriod: 7,
      penaltyRate: 2,
    },
    savings: {
      defaultInterestRate: 2.5,
      minimumBalance: 1000,
      withdrawalLimit: 500000,
    },
  });

  const handleSave = (section) => {
    // In real app, send to API
    console.log(`Saving ${section} settings:`, settings[section]);
    alert('Paramètres sauvegardés avec succès !');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Paramètres</h1>
          <p className="mt-1 text-sm text-gray-600">
            Configuration du système de microfinance
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Settings Sidebar */}
        <div className="lg:col-span-1">
          <div className="card">
            <div className="p-4">
              <nav className="space-y-1">
                <button
                  onClick={() => setActiveTab('general')}
                  className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md ${
                    activeTab === 'general'
                      ? 'bg-primary-50 text-primary-700'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <Cog6ToothIcon className="h-5 w-5 mr-3" />
                  Général
                </button>
                <button
                  onClick={() => setActiveTab('loan')}
                  className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md ${
                    activeTab === 'loan'
                      ? 'bg-primary-50 text-primary-700'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <CurrencyDollarIcon className="h-5 w-5 mr-3" />
                  Prêts
                </button>
                <button
                  onClick={() => setActiveTab('savings')}
                  className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md ${
                    activeTab === 'savings'
                      ? 'bg-primary-50 text-primary-700'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <BuildingOfficeIcon className="h-5 w-5 mr-3" />
                  Épargne
                </button>
                <button
                  onClick={() => setActiveTab('security')}
                  className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md ${
                    activeTab === 'security'
                      ? 'bg-primary-50 text-primary-700'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <ShieldCheckIcon className="h-5 w-5 mr-3" />
                  Sécurité
                </button>
                <button
                  onClick={() => setActiveTab('notifications')}
                  className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md ${
                    activeTab === 'notifications'
                      ? 'bg-primary-50 text-primary-700'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <ClockIcon className="h-5 w-5 mr-3" />
                  Notifications
                </button>
                <button
                  onClick={() => setActiveTab('reports')}
                  className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md ${
                    activeTab === 'reports'
                      ? 'bg-primary-50 text-primary-700'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <DocumentTextIcon className="h-5 w-5 mr-3" />
                  Rapports
                </button>
              </nav>
            </div>
          </div>
        </div>

        {/* Settings Content */}
        <div className="lg:col-span-3">
          <div className="card">
            <div className="p-6">
              {activeTab === 'general' && (
                <div className="space-y-6">
                  <h2 className="text-lg font-medium text-gray-900">Paramètres Généraux</h2>
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Nom de l'organisation
                      </label>
                      <input
                        type="text"
                        className="input-field mt-1"
                        value={settings.general.organizationName}
                        onChange={(e) => setSettings({
                          ...settings,
                          general: { ...settings.general, organizationName: e.target.value }
                        })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Devise
                      </label>
                      <select
                        className="input-field mt-1"
                        value={settings.general.currency}
                        onChange={(e) => setSettings({
                          ...settings,
                          general: { ...settings.general, currency: e.target.value }
                        })}
                      >
                        <option value="HTG">Gourde (HTG)</option>
                        <option value="USD">Dollar US ($)</option>
                        <option value="EUR">Euro (€)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Fuseau horaire
                      </label>
                      <select
                        className="input-field mt-1"
                        value={settings.general.timezone}
                        onChange={(e) => setSettings({
                          ...settings,
                          general: { ...settings.general, timezone: e.target.value }
                        })}
                      >
                        <option value="Africa/Dakar">Dakar (UTC)</option>
                        <option value="Africa/Abidjan">Abidjan (UTC)</option>
                        <option value="Africa/Lagos">Lagos (UTC+1)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Format de date
                      </label>
                      <select
                        className="input-field mt-1"
                        value={settings.general.dateFormat}
                        onChange={(e) => setSettings({
                          ...settings,
                          general: { ...settings.general, dateFormat: e.target.value }
                        })}
                      >
                        <option value="DD/MM/YYYY">JJ/MM/AAAA</option>
                        <option value="MM/DD/YYYY">MM/JJ/AAAA</option>
                        <option value="YYYY-MM-DD">AAAA-MM-JJ</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Langue
                      </label>
                      <select
                        className="input-field mt-1"
                        value={settings.general.language}
                        onChange={(e) => setSettings({
                          ...settings,
                          general: { ...settings.general, language: e.target.value }
                        })}
                      >
                        <option value="fr">Français</option>
                        <option value="en">English</option>
                        <option value="es">Español</option>
                      </select>
                    </div>
                  </div>
                  <div className="pt-6 border-t border-gray-200">
                    <button
                      onClick={() => handleSave('general')}
                      className="btn-primary"
                    >
                      Sauvegarder les paramètres
                    </button>
                  </div>
                </div>
              )}

              {activeTab === 'loan' && (
                <div className="space-y-6">
                  <h2 className="text-lg font-medium text-gray-900">Paramètres des Prêts</h2>
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Taux d'intérêt par défaut (%)
                      </label>
                      <input
                        type="number"
                        step="0.1"
                        className="input-field mt-1"
                        value={settings.loan.defaultInterestRate}
                        onChange={(e) => setSettings({
                          ...settings,
                          loan: { ...settings.loan, defaultInterestRate: parseFloat(e.target.value) }
                        })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Montant maximum du prêt (HTG)
                      </label>
                      <input
                        type="number"
                        className="input-field mt-1"
                        value={settings.loan.maxLoanAmount}
                        onChange={(e) => setSettings({
                          ...settings,
                          loan: { ...settings.loan, maxLoanAmount: parseInt(e.target.value) }
                        })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Durée minimum (mois)
                      </label>
                      <input
                        type="number"
                        className="input-field mt-1"
                        value={settings.loan.minLoanTerm}
                        onChange={(e) => setSettings({
                          ...settings,
                          loan: { ...settings.loan, minLoanTerm: parseInt(e.target.value) }
                        })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Durée maximum (mois)
                      </label>
                      <input
                        type="number"
                        className="input-field mt-1"
                        value={settings.loan.maxLoanTerm}
                        onChange={(e) => setSettings({
                          ...settings,
                          loan: { ...settings.loan, maxLoanTerm: parseInt(e.target.value) }
                        })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Période de grâce (jours)
                      </label>
                      <input
                        type="number"
                        className="input-field mt-1"
                        value={settings.loan.gracePeriod}
                        onChange={(e) => setSettings({
                          ...settings,
                          loan: { ...settings.loan, gracePeriod: parseInt(e.target.value) }
                        })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Taux de pénalité (% par jour)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        className="input-field mt-1"
                        value={settings.loan.penaltyRate}
                        onChange={(e) => setSettings({
                          ...settings,
                          loan: { ...settings.loan, penaltyRate: parseFloat(e.target.value) }
                        })}
                      />
                    </div>
                  </div>
                  <div className="pt-6 border-t border-gray-200">
                    <button
                      onClick={() => handleSave('loan')}
                      className="btn-primary"
                    >
                      Sauvegarder les paramètres
                    </button>
                  </div>
                </div>
              )}

              {activeTab === 'savings' && (
                <div className="space-y-6">
                  <h2 className="text-lg font-medium text-gray-900">Paramètres d'Épargne</h2>
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Taux d'intérêt par défaut (% annuel)
                      </label>
                      <input
                        type="number"
                        step="0.1"
                        className="input-field mt-1"
                        value={settings.savings.defaultInterestRate}
                        onChange={(e) => setSettings({
                          ...settings,
                          savings: { ...settings.savings, defaultInterestRate: parseFloat(e.target.value) }
                        })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Solde minimum (HTG)
                      </label>
                      <input
                        type="number"
                        className="input-field mt-1"
                        value={settings.savings.minimumBalance}
                        onChange={(e) => setSettings({
                          ...settings,
                          savings: { ...settings.savings, minimumBalance: parseInt(e.target.value) }
                        })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Limite de retrait (HTG)
                      </label>
                      <input
                        type="number"
                        className="input-field mt-1"
                        value={settings.savings.withdrawalLimit}
                        onChange={(e) => setSettings({
                          ...settings,
                          savings: { ...settings.savings, withdrawalLimit: parseInt(e.target.value) }
                        })}
                      />
                    </div>
                  </div>
                  <div className="pt-6 border-t border-gray-200">
                    <button
                      onClick={() => handleSave('savings')}
                      className="btn-primary"
                    >
                      Sauvegarder les paramètres
                    </button>
                  </div>
                </div>
              )}

              {activeTab === 'security' && (
                <div className="space-y-6">
                  <h2 className="text-lg font-medium text-gray-900">Paramètres de Sécurité</h2>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Durée de session (minutes)
                      </label>
                      <input
                        type="number"
                        className="input-field mt-1"
                        defaultValue="30"
                      />
                      <p className="mt-1 text-sm text-gray-500">
                        Durée d'inactivité avant déconnexion automatique
                      </p>
                    </div>
                    <div>
                      <label className="flex items-center">
                        <input type="checkbox" className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
                        <span className="ml-2 text-sm text-gray-700">
                          Exiger une authentification à deux facteurs
                        </span>
                      </label>
                    </div>
                    <div>
                      <label className="flex items-center">
                        <input type="checkbox" className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
                        <span className="ml-2 text-sm text-gray-700">
                          Limiter les tentatives de connexion
                        </span>
                      </label>
                    </div>
                  </div>
                  <div className="pt-6 border-t border-gray-200">
                    <button className="btn-primary">
                      Sauvegarder les paramètres
                    </button>
                  </div>
                </div>
              )}

              {activeTab === 'notifications' && (
                <div className="space-y-6">
                  <h2 className="text-lg font-medium text-gray-900">Paramètres de Notifications</h2>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-900">Notifications par email</p>
                        <p className="text-sm text-gray-500">Recevoir les notifications par email</p>
                      </div>
                      <input type="checkbox" className="toggle" defaultChecked />
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-900">Alertes de prêts en retard</p>
                        <p className="text-sm text-gray-500">Recevoir des alertes pour les prêts en retard</p>
                      </div>
                      <input type="checkbox" className="toggle" defaultChecked />
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-900">Rapports quotidiens</p>
                        <p className="text-sm text-gray-500">Recevoir un rapport quotidien</p>
                      </div>
                      <input type="checkbox" className="toggle" />
                    </div>
                  </div>
                  <div className="pt-6 border-t border-gray-200">
                    <button className="btn-primary">
                      Sauvegarder les paramètres
                    </button>
                  </div>
                </div>
              )}

              {activeTab === 'reports' && (
                <div className="space-y-6">
                  <h2 className="text-lg font-medium text-gray-900">Paramètres des Rapports</h2>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Format d'export par défaut
                      </label>
                      <select className="input-field mt-1">
                        <option value="pdf">PDF</option>
                        <option value="excel">Excel</option>
                        <option value="csv">CSV</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Fuseau horaire des rapports
                      </label>
                      <select className="input-field mt-1">
                        <option value="system">Fuseau du système</option>
                        <option value="utc">UTC</option>
                      </select>
                    </div>
                    <div>
                      <label className="flex items-center">
                        <input type="checkbox" className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" defaultChecked />
                        <span className="ml-2 text-sm text-gray-700">
                          Inclure les détails des transactions
                        </span>
                      </label>
                    </div>
                    <div>
                      <label className="flex items-center">
                        <input type="checkbox" className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" defaultChecked />
                        <span className="ml-2 text-sm text-gray-700">
                          Inclure les résumés par agence
                        </span>
                      </label>
                    </div>
                  </div>
                  <div className="pt-6 border-t border-gray-200">
                    <button className="btn-primary">
                      Sauvegarder les paramètres
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;